import { useCallback, useEffect, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Drawer, DrawerPosition, Heading, Paragraph } from '@savgroup-front-common/core';
import { Autocomplete, Input } from '@savgroup-front-common/core/src/atoms/Form';

import {
  SPARE_PARTS_CATALOG_OPTIONS,
  STOCK_PIECE_ETAT_OPTIONS,
} from '../../constants/sparePartsCatalog';
import { SUPERVISEUR_OPTIONS } from '../../constants/inventorySuperviseurOptions';
import type {
  StockAddPartsConfirmPayload,
  StockBulkDrawerStep,
  StockInventoryBulkConfirmPayload,
  StockPieceEtat,
  StockSelectionItem,
} from '../../types/stockBulk.types';
import {
  createEmptyAddPartLine,
  isAddPartLineComplete,
  parseAddPartLineQuantity,
  type StockAddPartLineForm,
} from './stockBulkAddPartsForm';
import {
  $ActionOption,
  $AddPartLineButtonWrap,
  $BackButton,
  $BackButtonWrap,
  $FieldBlock,
  $FieldLabel,
  $Footer,
  $Header,
  $Hint,
  $List,
  $PartLineCard,
  $PartLineCardHeader,
  $PartLineCardTitle,
  $PartLinesList,
  $RemovePartLineButton,
  $RequiredMark,
  $StepContent,
  $StepTitle,
} from './StockBulkActionDrawer.styles';

const DRAWER_WIDTH_PERCENT = 0.33;
const DRAWER_WIDTH_MIN = 380;
const DRAWER_WIDTH_MAX = 640;

const getDrawerWidthPx = (): number => {
  if (typeof window === 'undefined') {
    return 480;
  }
  const w = Math.round(window.innerWidth * DRAWER_WIDTH_PERCENT);

  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, w));
};

const getOptionValue = (option: unknown): string => {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return '';
  }

  const value = (option as { value?: unknown }).value;

  return typeof value === 'string' ? value : '';
};

const getSelectedOption = <T extends { value: string; label: string }>(
  options: T[],
  value: string,
): T | undefined => options.find((o) => o.value === value);

export interface StockBulkActionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStocks: StockSelectionItem[];
  onConfirmInventory?: (payload: StockInventoryBulkConfirmPayload) => void;
  onConfirmAddParts?: (payload: StockAddPartsConfirmPayload) => void;
}

const StockBulkActionDrawer = ({
  isOpen,
  onClose,
  selectedStocks,
  onConfirmInventory,
  onConfirmAddParts,
}: StockBulkActionDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidthPx);
  const [step, setStep] = useState<StockBulkDrawerStep>('choose');
  const [superviseur, setSuperviseur] = useState('');
  const [partLines, setPartLines] = useState<StockAddPartLineForm[]>([]);

  useEffect(() => {
    const updateWidth = () => setDrawerWidth(getDrawerWidthPx());
    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const resetFormState = useCallback(() => {
    setStep('choose');
    setSuperviseur('');
    setPartLines([]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetFormState();
    }
  }, [isOpen, resetFormState]);

  const handleClose = useCallback(() => {
    resetFormState();
    onClose();
  }, [onClose, resetFormState]);

  const handleBack = useCallback(() => {
    setStep('choose');
    setSuperviseur('');
    setPartLines([]);
  }, []);

  const handleSelectInventory = useCallback(() => {
    setStep('inventory');
  }, []);

  const handleSelectAddParts = useCallback(() => {
    setPartLines([createEmptyAddPartLine()]);
    setStep('add-parts');
  }, []);

  const handleAddPartLine = useCallback(() => {
    setPartLines((prev) => [...prev, createEmptyAddPartLine()]);
  }, []);

  const handleRemovePartLine = useCallback((lineId: string) => {
    setPartLines((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      return prev.filter((line) => line.id !== lineId);
    });
  }, []);

  const handleUpdatePartLine = useCallback(
    (lineId: string, patch: Partial<Omit<StockAddPartLineForm, 'id'>>) => {
      setPartLines((prev) =>
        prev.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
      );
    },
    [],
  );

  const handleConfirmInventory = useCallback(() => {
    const superviseurTrimmed = superviseur.trim();

    if (!superviseurTrimmed || selectedStocks.length === 0) {
      return;
    }

    onConfirmInventory?.({ superviseur: superviseurTrimmed, items: selectedStocks });
    handleClose();
  }, [handleClose, onConfirmInventory, selectedStocks, superviseur]);

  const handleConfirmAddParts = useCallback(() => {
    if (selectedStocks.length === 0 || partLines.length === 0) {
      return;
    }

    const allLinesComplete = partLines.every(isAddPartLineComplete);

    if (!allLinesComplete) {
      return;
    }

    onConfirmAddParts?.({
      lines: partLines.map((line) => ({
        catalogPieceId: line.catalogPieceId,
        quantity: parseAddPartLineQuantity(line),
        etatPiece: line.etatPiece as StockPieceEtat,
        emplacementStock: line.emplacement.trim(),
      })),
      items: selectedStocks,
    });
    handleClose();
  }, [handleClose, onConfirmAddParts, partLines, selectedStocks]);

  const canConfirmInventory = selectedStocks.length > 0 && superviseur.trim() !== '';
  const canConfirmAddParts =
    selectedStocks.length > 0 && partLines.length > 0 && partLines.every(isAddPartLineComplete);

  const drawerTitle =
    step === 'choose'
      ? 'Action sur les stocks sélectionnés'
      : step === 'inventory'
        ? 'Lancer un inventaire'
        : 'Ajouter des pièces';

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      position={DrawerPosition.Right}
      width={drawerWidth}
      dataTestId="stock-bulk-action-drawer"
    >
      <$Header>
        <Heading level={2}>{drawerTitle}</Heading>
        <Button
          type={BUTTON_TYPES.BUTTON}
          tertiary
          onClick={handleClose}
          ariaLabel={{ id: 'stock.bulk.drawer.close', defaultMessage: 'Fermer' }}
        >
          ✕
        </Button>
      </$Header>

      {step !== 'choose' && (
        <$BackButtonWrap>
          <$BackButton type="button" onClick={handleBack}>
            ← Retour au choix d&apos;action
          </$BackButton>
        </$BackButtonWrap>
      )}

      {selectedStocks.length > 0 && (
        <$List>
          {selectedStocks.map((s) => (
            <li key={s.idStock}>
              <strong>{s.nomStock}</strong>
              <span style={{ color: '#64748b' }}> — {s.siteAssocie}</span>
            </li>
          ))}
        </$List>
      )}

      {step === 'choose' && (
        <>
          <$Hint style={{ marginTop: '0.75rem' }}>
            {selectedStocks.length} stock(s) sélectionné(s). Choisissez l&apos;action à effectuer.
          </$Hint>
          <$StepTitle style={{ marginTop: '1.25rem' }}>Que souhaitez-vous faire ?</$StepTitle>
          <$StepContent>
            <$ActionOption type="button" onClick={handleSelectAddParts} data-testid="stock-bulk-action-add-parts">
              Ajouter des pièces
            </$ActionOption>
            <$ActionOption
              type="button"
              onClick={handleSelectInventory}
              data-testid="stock-bulk-action-launch-inventory"
            >
              Lancer un inventaire
            </$ActionOption>
          </$StepContent>
          <$Footer>
            <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleClose}>
              Annuler
            </Button>
          </$Footer>
        </>
      )}

      {step === 'inventory' && (
        <>
          <Paragraph style={{ marginTop: '0.75rem', marginBottom: 0, color: '#64748b', fontSize: '0.875rem' }}>
            Les inventaires seront créés pour les stocks sélectionnés. Ceci est une action de démonstration (POC).
          </Paragraph>
          <$FieldBlock>
            <$FieldLabel>Superviseur</$FieldLabel>
            <Autocomplete
              name="stock-bulk-inventory-superviseur"
              options={SUPERVISEUR_OPTIONS}
              value={getSelectedOption(SUPERVISEUR_OPTIONS, superviseur)}
              placeholder="Sélectionner un superviseur"
              onChange={(option) => {
                setSuperviseur(getOptionValue(option));
              }}
            />
          </$FieldBlock>
          <$Footer>
            <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleClose}>
              Annuler
            </Button>
            <Button
              type={BUTTON_TYPES.BUTTON}
              primary
              onClick={handleConfirmInventory}
              disabled={!canConfirmInventory}
              dataTestId="stock-inventory-confirm"
            >
              Confirmer
            </Button>
          </$Footer>
        </>
      )}

      {step === 'add-parts' && (
        <>
          <$Hint style={{ marginTop: '0.75rem' }}>
            Ajoutez une ou plusieurs pièces distinctes. Chaque ligne sera créée dans tous les stocks
            sélectionnés.
          </$Hint>
          {partLines.length > 0 && (
            <$PartLinesList>
              {partLines.map((line, index) => (
                <$PartLineCard key={line.id}>
                  <$PartLineCardHeader>
                    <$PartLineCardTitle>Pièce {index + 1}</$PartLineCardTitle>
                    {partLines.length > 1 && (
                      <$RemovePartLineButton
                        type="button"
                        onClick={() => handleRemovePartLine(line.id)}
                        aria-label={`Retirer la pièce ${index + 1}`}
                      >
                        Retirer
                      </$RemovePartLineButton>
                    )}
                  </$PartLineCardHeader>
                  <$FieldBlock style={{ marginTop: 0 }}>
                    <$FieldLabel>
                      Pièce du catalogue
                      <$RequiredMark aria-hidden>*</$RequiredMark>
                    </$FieldLabel>
                    <Autocomplete
                      name={`stock-bulk-add-parts-catalog-${line.id}`}
                      options={SPARE_PARTS_CATALOG_OPTIONS}
                      value={getSelectedOption(SPARE_PARTS_CATALOG_OPTIONS, line.catalogPieceId)}
                      placeholder="Rechercher une pièce détachée…"
                      onChange={(option) => {
                        handleUpdatePartLine(line.id, { catalogPieceId: getOptionValue(option) });
                      }}
                    />
                  </$FieldBlock>
                  <$FieldBlock>
                    <$FieldLabel>
                      Quantité
                      <$RequiredMark aria-hidden>*</$RequiredMark>
                    </$FieldLabel>
                    <Input
                      name={`stock-bulk-add-parts-quantity-${line.id}`}
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(event) => {
                        handleUpdatePartLine(line.id, { quantity: event.target.value });
                      }}
                    />
                  </$FieldBlock>
                  <$FieldBlock>
                    <$FieldLabel>
                      État de la pièce
                      <$RequiredMark aria-hidden>*</$RequiredMark>
                    </$FieldLabel>
                    <Autocomplete
                      name={`stock-bulk-add-parts-etat-${line.id}`}
                      options={STOCK_PIECE_ETAT_OPTIONS}
                      value={getSelectedOption(STOCK_PIECE_ETAT_OPTIONS, line.etatPiece)}
                      placeholder="Neuf, reconditionné ou occasion"
                      onChange={(option) => {
                        handleUpdatePartLine(line.id, { etatPiece: getOptionValue(option) });
                      }}
                    />
                  </$FieldBlock>
                  <$FieldBlock>
                    <$FieldLabel>Emplacement</$FieldLabel>
                    <Input
                      name={`stock-bulk-add-parts-emplacement-${line.id}`}
                      value={line.emplacement}
                      placeholder="Ex. Rack A3 — Tiroir 2"
                      onChange={(event) => {
                        handleUpdatePartLine(line.id, { emplacement: event.target.value });
                      }}
                    />
                  </$FieldBlock>
                </$PartLineCard>
              ))}
            </$PartLinesList>
          )}
          <$AddPartLineButtonWrap>
            <Button
              type={BUTTON_TYPES.BUTTON}
              secondary
              onClick={handleAddPartLine}
              dataTestId="stock-add-parts-add-line"
            >
              + Ajouter des pièces
            </Button>
          </$AddPartLineButtonWrap>
          <$Footer>
            <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleClose}>
              Annuler
            </Button>
            <Button
              type={BUTTON_TYPES.BUTTON}
              primary
              onClick={handleConfirmAddParts}
              disabled={!canConfirmAddParts}
              dataTestId="stock-add-parts-confirm"
            >
              Ajouter les pièces
            </Button>
          </$Footer>
        </>
      )}
    </Drawer>
  );
};

export default StockBulkActionDrawer;
